import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { formatCodeword } from '@airreading/common/error';

import { type AuthFailure, authFailurePresentation } from '@/auth/auth-errors.js';
import { Button } from '@/components/ui/button.js';

export interface SchoolPortalWindow {
  closed: boolean;
  close: () => void;
  history: Pick<History, 'back' | 'length'>;
}

export function returnToSchoolPortal(browserWindow: SchoolPortalWindow = window): boolean {
  if (browserWindow.history.length > 1) {
    browserWindow.history.back();
    return true;
  }

  browserWindow.close();
  return browserWindow.closed;
}

export interface LoginErrorProps {
  failure: AuthFailure;
  onStartGoogleSignIn: () => void;
  returnToPortal?: () => boolean;
  reloadApplication?: () => void;
}

export function LoginError({
  failure,
  onStartGoogleSignIn,
  returnToPortal = returnToSchoolPortal,
  reloadApplication = () => window.location.reload(),
}: LoginErrorProps) {
  const presentation = authFailurePresentation(failure);
  const [showCloseInstruction, setShowCloseInstruction] = useState(false);

  const handleAction = () => {
    switch (presentation.action) {
      case 'start_google_sign_in':
        onStartGoogleSignIn();
        return;
      case 'return_to_school_portal':
        setShowCloseInstruction(!returnToPortal());
        return;
      case 'reload_application':
        reloadApplication();
        return;
      case 'none':
        return;
    }
  };

  return (
    <section
      className="w-full text-left md:text-center"
      data-testid="auth-error-view"
      aria-labelledby="auth-error-title"
    >
      <img src="/logo.svg" alt="Air Reading" className="w-40 md:w-48 md:mx-auto" />
      <AlertCircle aria-hidden="true" className="mx-auto mt-10 size-12 text-destructive" />
      <h1 id="auth-error-title" className="mt-4 text-4xl font-extrabold text-air-primary md:text-5xl">
        {presentation.title}
      </h1>
      <p className="mt-6 text-lg leading-7 text-slate-700">{presentation.description}</p>
      <p className="mt-6 font-semibold text-slate-700" data-testid="auth-error-codeword">
        Error code: <span className="whitespace-nowrap">{formatCodeword(presentation.codeword)}</span>
      </p>
      {showCloseInstruction ? (
        <p className="mt-6 text-lg" role="status" aria-live="polite">
          Close this tab and return to your school portal.
        </p>
      ) : null}
      {presentation.action !== 'none' ? (
        <Button
          type="button"
          onClick={handleAction}
          className="mt-10 w-full rounded-lg px-6 py-6 text-lg font-semibold"
          data-testid="auth-error-button-action"
        >
          {presentation.actionLabel}
        </Button>
      ) : null}
    </section>
  );
}
